<template>
	<div class="grid grid-cols-1 gap-12 pt-8 mx-auto">
		<div class="flex justify-center w-full max-w-5xl px-4 component-container">
			<Checklist
				:image="ANIMATED_TRANSMIT_IMAGE"
				:features="features1"
				:features2="features2"
				:title="$t('about.features-title')"
				text-max-width="max-w-auto"
				image-max-width="max-w-lg"
				use-html
				single-column
			>
				<AboutLogos class="max-w-3xl px-6 py-12 component-container" />
			</Checklist>
		</div>
		<div class="grid gap-10 pt-8 pb-12 -mt-24 text-left md:mt-0">
			<div class="grid w-full max-w-5xl grid-cols-1 pb-0 border-t-2 border-gray-200 component-container">
				<LearnMore
					v-for="(section, index) in sections"
					:key="section.title"
					:data="section"
					:position="getPosition('right', index)"
					class="md:mt-6 lg:mt-16"
				/>
			</div>
			<CarouselTestimonial />
			<Faq
				class="max-w-5xl px-8 component-container"
				:faqs="faqs"
			/>
			<CarouselBlog />
			<div class="flex flex-col items-center content-center text-center lg:mx-16 md:justify-center md:space-x-20 md:flex-row">
				<UILinkButton
					:to="localePath('/service-areas')"
					:text="$t('service-areas.title')"
					:top-text="$t('service-areas.link-top-text')"
				/>
				<UILinkButton
					:to="localePath('/careers')"
					:text="$t('careers.link-button-text')"
					:top-text="$t('careers.link-top-text')"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import {
	STORYBLOK_VERSION,
	COMPANY_NAME,
	COMPANY_PHONE,
	BASE_URL
} from '@/constants/index.js'
import {
	JSON_LD_LOGO,
	JSON_LD_ORGANIZATION_SAME_AS,
	JSON_LD_ORGANIZATION_REVIEW,
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_ADDRESS
} from '@/constants/json-ld'
import type { FaqProps } from '@/components/faq/types'
import type { ChecklistProps } from '@/components/checklist/types'
import { mapLearnMore, getPosition } from '@/utils/learnMoreMap'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'

const route = useRoute()

const { getStory } = useStoryblokWithErrorReporting()

const { t, tm, locale } = useI18n({
	useScope: 'global'
})
const localePath = useLocalePath()

useHead({
	title: t('about.meta-title'),
	meta: [
		{ name: 'description', content: t('about.meta-description', { COMPANY_NAME }) },
		{ name: 'url', content: `${BASE_URL}/about` }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicon.ico'
		}
	]
})

const story = await getStory('about', {
	version: STORYBLOK_VERSION,
	language: locale.value
})

const { title, description } = story.value.content?.metatags

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => '/images/logo.svg',
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/logo.svg',
	twitterCard: () => 'summary_large_image'
})

const sections = mapLearnMore(story.value?.content?.learnMoreContent)

const faqs: FaqProps[] = tm('faqs')
const features1: ChecklistProps[] = tm('about.features1')
const features2: ChecklistProps[] = tm('about.features2')

const ANIMATED_TRANSMIT_IMAGE = {
	id: 'about-animated-transmit',
	src: '/images/parker.webp',
	srcset: '/images/parker-sm.webp 400w, /images/parker.webp 800w',
	alt: t('about.image-alt')
}

const storyblokApi = useStoryblokApi()
const { data } = await useAsyncData(
	`service-areas-${locale.value}`,
	async () => await storyblokApi.get('cdn/stories', {
		version: STORYBLOK_VERSION,
		starts_with: 'service-areas',
		is_startpage: 0,
		language: locale.value
	})
)
if (!data.value || !data.value?.data?.stories?.length) {
	reportError('No service areas found')
}
const stories = computed(() => data?.value?.data?.stories)

const areasJsonLd = computed(() => {
	return stories?.value?.map((story: any) => {
		const { city, stateShort } = story?.content?.cityCardContent?.[0]
		return {
			'@type': 'Place',
			address: {
				'@type': 'PostalAddress',
				addressLocality: city,
				addressRegion: stateShort
			},
			potentialAction: {
				'@type': 'SearchAction',
				query: `Internet services in ${city}, ${stateShort}`
			}
		}
	})
})

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: BASE_URL
				}, {
					'@type': 'ListItem',
					position: 2,
					name: 'About',
					item: `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Organization',
			name: COMPANY_NAME,
			logo: JSON_LD_LOGO,
			url: BASE_URL,
			telephone: COMPANY_PHONE,
			description: t('meta-description', { COMPANY_NAME }),
			contactPoint: JSON_LD_ORGANIZATION_CONTACT_POINT,
			sameAs: JSON_LD_ORGANIZATION_SAME_AS,
			address: JSON_LD_ORGANIZATION_ADDRESS,
			review: JSON_LD_ORGANIZATION_REVIEW,
			areaServed: [
				...areasJsonLd.value
			],
			hasOfferCatalog: JSON_LD_HAS_OFFER_CATALOG
		}
	]
})

</script>
